// The pilot panel's onTrajectoryToggle wiring, the predicted-path verb, the
// time warp control and the land verb: four independent pieces main.ts still
// builds in this order, none sharing state with another, so one file holds
// all four rather than four near-empty ones.
import * as THREE from 'three';
import { createPilotPanel, type PilotPanel } from '../pilotPanel';
import {
  createLandVerb, type AtlasWorldHandle, type LandVerbDeps, type LandVerbHandle,
} from '../sim/landVerb';
import { LAUNCH_KEY } from './flightField';
import {
  createTrajectory,
  type TrajectoryBuild, type TrajectoryHandle, type TrajectoryStatus,
} from '../sim/trajectory';
import {
  createWarp, viewWarpFrozen, warpAnyAxis, WARP_APPROACH_FACTOR,
  warpApproachNoCeiling, warpDropPoint, warpStaleRate, warpStick, warpSweepHelio,
  warpThrustDrop, type WarpDeps, type WarpEnvelope, type WarpHandle,
} from '../sim/warp';
import { gameClock, WARP_LEVELS, WARP_RAMP_SECONDS } from '../world/clock';
import { gameState } from '../state/gameState';
import { heliocentric } from '../world/ephemeris';
import type { WorldVec } from '../world/frame';
import type { BodyRow } from '../world/types';
import type { FlightConfig, FlightPull, FlightState, FlightVec } from '../sim/flight';
import type { BodyPlaces } from '../sim/bodies';
import { TEST_SWITCHES } from '../urlFlags.ts';

// ---------- Pilot panel ----------

export interface CruisePilotPanelDeps {
  onSolarSystem: () => void;
  /** `trajectory` is built after the pilot panel; main.ts passes a
   *  `get trajectory()`-style getter so the toggle reads the live handle. */
  readonly trajectory: { readonly enabled: boolean; setEnabled(on: boolean): void };
}

export function createCruisePilotPanel(deps: CruisePilotPanelDeps): PilotPanel {
  return createPilotPanel({
    onTrajectoryToggle: () => { deps.trajectory.setEnabled(!deps.trajectory.enabled); },
    onSolarSystem: deps.onSolarSystem,
  });
}

// ---------- The land verb ----------
//
// `src/sim/landVerb.ts` owns the verb: the Atlas host mounted at boot, the
// camera handover, the landed pin and the way back out. main.ts still owns
// the Atlas mount reader (`atlasCamera`, `atlasWorld`) and forwards it in.

// `?landframepull=1` pins the landed ship to the gravity pick's body rather
// than the body LAND opened on. The two differ on a moon whose hold radius
// sits under its own surface, so the pin goes on the planet and the moon
// leaves without the ship. `--ship --inject land-frame-pull` opens it; no
// player reaches it.
const LAND_FRAME_PULL = TEST_SWITCHES && new URLSearchParams(location.search).get('landframepull') === '1';

export type CruiseLandVerbDeps = Omit<LandVerbDeps, 'LAND_FRAME_PULL' | 'LAUNCH_KEY'>;

export function createCruiseLandVerb(deps: CruiseLandVerbDeps): LandVerbHandle {
  return createLandVerb({
    simPos: deps.simPos, flight: deps.flight, simWorld: deps.simWorld,
    attemptLaunch: deps.attemptLaunch, atlasCamera: deps.atlasCamera, atlasWorld: deps.atlasWorld,
    LAND_FRAME_PULL, LAUNCH_KEY,
    atlasGround: deps.atlasGround,
    get simBodies() { return deps.simBodies; },
    get bodyRows() { return deps.bodyRows; },
    get frameParentId() { return deps.frameParentId; },
    get seedJd() { return deps.seedJd; },
    get hudButtons() { return deps.hudButtons; },
  });
}

// ---------- The ship's own predicted path ----------
//
// Off until the pilot asks for it. `src/sim/trajectory.ts` owns the prediction
// and the line; what is owned here is the switch, the clock and the pulling
// set the prediction is handed, and where the line is registered - the Atlas's
// own camera-relative frame, beside the orbit rings, so it is drawn in cruise
// and in the Solar System view without a second mount.
const _trajParams = new URLSearchParams(location.search);
const TRAJ_BALLISTIC = TEST_SWITCHES && _trajParams.get('trajballistic') === '1';
const TRAJ_RUNOUT = TEST_SWITCHES && _trajParams.get('trajrunout') === '1';
// `?trajcaptionatlas=1`: keep the far end's caption drawn in the Solar System
// Atlas view, which is the fault `syncTrajectoryCaption` below exists to
// close - a GROUND or FORECAST ENDS box the size of a HUD panel, floating
// over a body nobody is flying near. The control `orbit-rings.mjs
// --trajectory --inject traj-caption-atlas` opens it. Nothing a player
// reaches sets it.
const TRAJ_CAPTION_ATLAS = TEST_SWITCHES && _trajParams.get('trajcaptionatlas') === '1';
// `?trajcaptionoff=1`: suppress the far end's caption in every view, ship sim
// included. The other side of the same control - `orbit-rings.mjs
// --trajectory --inject traj-caption-off` - so the gate's own bar has a
// fault that drops the caption everywhere to hold against, not only one that
// adds it back where it should be gone. Nothing a player reaches sets it.
const TRAJ_CAPTION_OFF = TEST_SWITCHES && _trajParams.get('trajcaptionoff') === '1';
// `?trajanchorpinned=1`: register the line at the frame parent's build-time
// place instead of its live one, so the parent walks out from under the
// drawing. `orbit-rings.mjs --trajectory --inject traj-anchor-pinned` opens it.
const TRAJ_ANCHOR_PINNED = TEST_SWITCHES && _trajParams.get('trajanchorpinned') === '1';
// `?lineanchored=1` is the same fault under the viewwarp arm's own name.
// `?trajbodiespinned=1`: sample the other bodies once at the rebuild and hold
// them there, which is what the prediction did before it moved with them.
// `orbit-rings.mjs --trajectory --inject traj-bodies-pinned` opens it.
const TRAJ_BODIES_PINNED = TEST_SWITCHES && _trajParams.get('trajbodiespinned') === '1';
// `?trajhorizononeperiod=1`: zero the bound horizon's margin, restoring the
// exact one-period horizon a closed loop used to end short against.
// `orbit-rings.mjs --trajectory --inject horizon-one-period` opens it.
const TRAJ_HORIZON_ONE_PERIOD = TEST_SWITCHES && _trajParams.get('trajhorizononeperiod') === '1';
// `?trajslowrebuild=1`: add a fixed 24 ms inside the rebuild itself, so the
// rebuild's own cost, the frame that carried it and nothing else move.
// `orbit-rings.mjs --trajectory --inject traj-slow-rebuild` opens it.
const TRAJ_SLOW_REBUILD = TEST_SWITCHES && _trajParams.get('trajslowrebuild') === '1';
// `?trajslowidle=1`: the mirror of the above, added to every frame that does
// NOT carry a rebuild, so the box reads loud while the rebuild stays cheap.
// `orbit-rings.mjs --trajectory --inject traj-slow-idle` opens it.
const TRAJ_SLOW_IDLE = TEST_SWITCHES && _trajParams.get('trajslowidle') === '1';
// `?trajclosenosnap=1`: leave a closed loop's drawn far end at the closest
// approach the closure gate sampled instead of putting it on the ship.
// `orbit-rings.mjs --trajectory --inject close-no-snap` opens it.
const TRAJ_CLOSE_NO_SNAP = TEST_SWITCHES && _trajParams.get('trajclosenosnap') === '1';
// `?trajclosenoresample=1`: snap a closed loop's far end onto the ship without
// laying the flown arc between the last kept knot and it, so the closing
// segment is a chord again.
// `orbit-rings.mjs --trajectory --inject close-no-resample` opens it.
const TRAJ_CLOSE_NO_RESAMPLE = TEST_SWITCHES && _trajParams.get('trajclosenoresample') === '1';
// `?trajcaptionperrebuild=1`: repaint the far end's caption on every rebuild
// and drop it on every other one, so it blinks and re-inks at the rebuild rate.
// `orbit-rings.mjs --trajectory --inject caption-per-rebuild` opens it.
const TRAJ_CAPTION_PER_REBUILD = TEST_SWITCHES && _trajParams.get('trajcaptionperrebuild') === '1';
// `?lineanchored=1` re-anchors the predicted path only when it rebuilds, so
// between two rebuilds it stands still in space while the body it is drawn
// round keeps moving and it snaps back on the next one. The
// broken-on-purpose variant `--viewwarp --inject line-anchored` opens it,
// where the carry's own reading must fail. Nothing a player reaches sets it.
// Read by both the trajectory build and the warp debug bus below - the only
// two readers, which is why it lives here rather than in main.ts.
const LINE_ANCHORED = TEST_SWITCHES && new URLSearchParams(location.search).get('lineanchored') === '1';
// `?linelagframe=1` hangs the line on its parent's place one frame late, the line
// drawn a frame behind the planet; `--viewwarp --inject line-lag-frame` opens it.
const LINE_LAG_FRAME = TEST_SWITCHES && new URLSearchParams(location.search).get('linelagframe') === '1';

export interface CruiseTrajectoryDeps {
  flightConfig: FlightConfig;
  flight: FlightState;
  scene: THREE.Scene;
  atlasWorld: () => AtlasWorldHandle | null;
  /** The registry's own rows behind each pulling slot, in slot order. */
  readonly pullRows: ReadonlyArray<BodyRow | null>;
  /** The slot's own held gravity-pull centre, for a slot whose row is still
   *  null or whose epoch is not yet finite. */
  readonly _pulls: readonly FlightPull[];
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
  readonly seedJd: number;
}

export interface CruiseTrajectoryHandle {
  trajectory: TrajectoryHandle;
  /**
   * Slot `i`'s heliocentric centre and velocity `aheadS` sim seconds past the
   * registry's own epoch, for the prediction's sampling grid.
   */
  sampleTrajectoryBody(i: number, aheadS: number, out: WorldVec, vel: WorldVec): void;
  /**
   * The far end's caption only in the ship sim view, never in the Solar
   * System Atlas view. Call from both branches of the tick.
   */
  syncTrajectoryCaption(): void;
}

export function createCruiseTrajectory(deps: CruiseTrajectoryDeps): CruiseTrajectoryHandle {
  const { flightConfig, flight, scene, atlasWorld } = deps;

  const _trajSample = { position: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 } };
  /**
   * Slot `i`'s heliocentric centre and velocity `aheadS` sim seconds past the
   * registry's own epoch, for the prediction's sampling grid.
   *
   * The same `heliocentric()` the registry itself is placed by, so a body the
   * line is drawn against and the body the Atlas draws cannot be two different
   * solar systems. Null under `?bodiespinned=1`, where the registry does not
   * advance and a prediction that moved the bodies would be predicting a world
   * nothing else is in.
   */
  function sampleTrajectoryBody(i: number, aheadS: number, out: WorldVec, vel: WorldVec): void {
    const row = deps.pullRows[i] ?? null;
    if (row === null || !Number.isFinite(deps.seedJd)) {
      const held = deps._pulls[i]?.at;
      out.x = held?.x ?? 0;
      out.y = held?.y ?? 0;
      out.z = held?.z ?? 0;
      vel.x = 0;
      vel.y = 0;
      vel.z = 0;
      return;
    }
    const st = heliocentric(row, deps.bodyRows, deps.seedJd + aheadS / 86400, _trajSample);
    out.x = st.position.x * 1000;
    out.y = st.position.y * 1000;
    out.z = st.position.z * 1000;
    vel.x = st.velocity.x * 1000;
    vel.y = st.velocity.y * 1000;
    vel.z = st.velocity.z * 1000;
  }

  const _lagged = new Map<string, { jd: number; now: WorldVec; was: WorldVec }>();
  /** A body's place at the epoch the registry stood at one frame before this one. */
  function bodyAtLagged(id: string): WorldVec | null {
    const now = deps.simBodies?.worldAt(id) ?? null;
    if (now === null) return null;
    const e = _lagged.get(id);
    if (e === undefined) {
      const first = { jd: deps.seedJd, now: { ...now }, was: { ...now } };
      _lagged.set(id, first);
      return first.was;
    }
    if (e.jd !== deps.seedJd) {
      e.was.x = e.now.x;
      e.was.y = e.now.y;
      e.was.z = e.now.z;
      e.now.x = now.x;
      e.now.y = now.y;
      e.now.z = now.z;
      e.jd = deps.seedJd;
    }
    return e.was;
  }

  const trajectory: TrajectoryHandle = createTrajectory({
    config: flightConfig,
    ballistic: TRAJ_BALLISTIC,
    lagOneFrame: TEST_SWITCHES && _trajParams.get('trajlag') === '1',
    rebuildStale: TEST_SWITCHES && _trajParams.get('trajrebuildstale') === '1',
    noResidue: TEST_SWITCHES && _trajParams.get('trajnoresidue') === '1',
    runOut: TRAJ_RUNOUT,
    bodyAt: LINE_LAG_FRAME ? bodyAtLagged : (id: string): WorldVec | null => deps.simBodies?.worldAt(id) ?? null,
    pinnedAnchor: TRAJ_ANCHOR_PINNED || LINE_ANCHORED,
    pinnedBodies: TRAJ_BODIES_PINNED,
    horizonOnePeriod: TRAJ_HORIZON_ONE_PERIOD,
    slowRebuild: TRAJ_SLOW_REBUILD,
    slowIdle: TRAJ_SLOW_IDLE,
    closeNoSnap: TRAJ_CLOSE_NO_SNAP,
    closeNoResample: TRAJ_CLOSE_NO_RESAMPLE,
    captionPerRebuild: TRAJ_CAPTION_PER_REBUILD,
    groundDatum: TEST_SWITCHES && _trajParams.get('trajdatumground') === '1',
    catmullRom: TEST_SWITCHES && _trajParams.get('trajcatmullrom') === '1',
    planetFrame: TEST_SWITCHES && _trajParams.get('trajplanetframe') === '1',
    chordEnd: TEST_SWITCHES && _trajParams.get('trajchordend') === '1',
  });
  const _trajAnchor: WorldVec = { x: 0, y: 0, z: 0 };
  const detachTrajectory = atlasWorld()?.attach(trajectory.line, () => {
    const a = trajectory.anchor();
    // `eclipticToScene` written out, so the per-frame read allocates nothing.
    _trajAnchor.x = a.x;
    _trajAnchor.y = a.z;
    _trajAnchor.z = -a.y;
    return _trajAnchor;
  }) ?? null;
  window.addEventListener('pagehide', () => {
    detachTrajectory?.();
    trajectory.dispose();
  }, { once: true });

  /**
   * The far end's caption only in the ship sim view, never in the Solar System
   * Atlas view: the line and its point mark stay drawn either way (#348 asked
   * for the line there and Michael confirmed it), but a text box the size of a
   * HUD panel reads as clutter once the camera has pulled back to browse the
   * system. `atlasWorld().external` is exactly that distinction already - true
   * while cruise co-draws the Atlas's world, false once the Atlas takes its own
   * camera for the Solar System pane or the land verb's descent. Called from
   * both branches of the tick so it keeps up while the menu/pane is open and the
   * rest of this frame's block below is skipped.
   */
  function syncTrajectoryCaption(): void {
    const simView = atlasWorld()?.external ?? true;
    // A ship already at rest on a surface has a path that meets the ground at
    // once, so the caption reads GROUND in nought seconds over the hull it is
    // sitting on: an alarm for something that has already happened and that the
    // notice and the pilot panel's own row both say in words.
    const resting = flight.grounded !== null;
    trajectory.setCaptionVisible(
      (simView || TRAJ_CAPTION_ATLAS) && !TRAJ_CAPTION_OFF && !resting);
  }

  // Read and driven by `tools/preview/orbit-rings.mjs --trajectory`. `setEnabled`
  // is also the setter the Solar System view's own layer panel wires to; the
  // wiring is that lane's, the switch is this one's.
  scene.userData['simTrajectory'] = {
    get enabled(): boolean { return trajectory.enabled; },
    get ballistic(): boolean { return TRAJ_BALLISTIC; },
    get runOut(): boolean { return TRAJ_RUNOUT; },
    get captionAtlas(): boolean { return TRAJ_CAPTION_ATLAS; },
    get captionOff(): boolean { return TRAJ_CAPTION_OFF; },
    get anchorPinned(): boolean { return TRAJ_ANCHOR_PINNED; },
    get bodiesPinned(): boolean { return TRAJ_BODIES_PINNED; },
    get closeNoSnap(): boolean { return TRAJ_CLOSE_NO_SNAP; },
    get closeNoResample(): boolean { return TRAJ_CLOSE_NO_RESAMPLE; },
    get captionPerRebuild(): boolean { return TRAJ_CAPTION_PER_REBUILD; },
    /** Where the line is registered THIS frame: the parent's live centre plus the
     *  ship's parent-relative place at the build. */
    anchor(): WorldVec { const a = trajectory.anchor(); return { x: a.x, y: a.y, z: a.z }; },
    /** What the last rebuilds cost, ms, newest last. */
    buildCosts(): number[] { return trajectory.buildCosts(); },
    /** Rebuilds so far. A frame this grows on is a frame that carried one. */
    builds(): number { return trajectory.builds(); },
    resetCosts(): void { trajectory.resetCosts(); },
    /** Live toggle for the idle-frame busy wait; the cost leg disarms it before its re-sample. */
    setSlowIdle(on: boolean): void { trajectory.setSlowIdle(on); },
    /** The registry's own epoch this frame, so a reader can measure a sim day
     *  in the branch where the menu is open and `simSeconds` does not advance. */
    get simJd(): number { return deps.seedJd; },
    setEnabled(on: boolean): void { trajectory.setEnabled(on); },
    /** Pin the horizon in sim seconds; 0 or less gives the derived one back. */
    setHorizon(s: number): void { trajectory.setHorizon(s); },
    status(): TrajectoryStatus { return trajectory.status(); },
    built(): TrajectoryBuild | null { return trajectory.built(); },
  };

  return { trajectory, sampleTrajectoryBody, syncTrajectoryCaption };
}

// ---------- Time warp: how fast the trip plays is the player's ----------
//
// `src/sim/warp.ts` owns the warp control, the automatic drops and the readout;
// `src/world/clock.ts` holds the levels and the ramp. What stays here is the
// state warp reads live off the rest of the file and the debug bus
// `tools/preview/descent-dojo.mjs --warp` reads off the scene.

// `?autopilotratecancels=1` counts a clock pick as a hand on the stick, the way
// it did before a pick became an ask the autopilot answers. The transit arm's
// rate-change-cancels control opens it; nothing a player reaches does.
const AUTOPILOT_RATE_CANCELS = TEST_SWITCHES && new URLSearchParams(location.search)
  .get('autopilotratecancels') === '1';

export interface CruiseWarpDeps extends Omit<WarpDeps, 'BODY_WORLD_VELOCITY' | 'AUTOPILOT_RATE_CANCELS' | 'heldCruiseThrottle'> {
  scene: THREE.Scene;
  readFlightPulls: () => void;
  BODIES_PINNED: boolean;
  FLIGHT_CARRY_OLD_PARENT: boolean;
  FLIGHT_CENTRES_HELD: boolean;
  MARKER_STALE: boolean;
  LINE_STALE: boolean;
}

export function createCruiseWarp(deps: CruiseWarpDeps): { warp: WarpHandle } {
  const {
    input, ship, vitals, simPos, scene, CLOCK_CONST,
    pickDominantPull, shipWorldAt, shipWorldVelocity, requestAutopilotWarp, notePilotInput,
    readFlightPulls, BODIES_PINNED, FLIGHT_CARRY_OLD_PARENT, FLIGHT_CENTRES_HELD,
    MARKER_STALE, LINE_STALE,
  } = deps;
  // Read when the registry has no velocity for a row: a body at rest.
  // `pilotTarget.ts` keeps its own copy of this fallback rather than sharing
  // this one.
  const BODY_WORLD_VELOCITY: FlightVec = { x: 0, y: 0, z: 0 };

  const warp = createWarp({
    input, ship, vitals, simPos, BODY_WORLD_VELOCITY, AUTOPILOT_RATE_CANCELS, CLOCK_CONST,
    pickDominantPull, shipWorldAt, shipWorldVelocity,
    requestAutopilotWarp, notePilotInput,
    get engagement() { return deps.engagement; },
    get autopilot() { return deps.autopilot; },
    get autopilotCmd() { return deps.autopilotCmd; },
    get autopilotWarpWish() { return deps.autopilotWarpWish; },
    set autopilotWarpWish(v: number | null) { deps.autopilotWarpWish = v; },
    get _pulls() { return deps._pulls; },
    get _pullIds() { return deps._pullIds; },
    get bodyRows() { return deps.bodyRows; },
    get simBodies() { return deps.simBodies; },
    get flightSimSeconds() { return deps.flightSimSeconds; },
    get heldCruiseThrottle() { return gameState().ship.flight.heldCruiseThrottle; },
  });

  // Read by `tools/preview/descent-dojo.mjs --warp`, beside `simFlight` and
  // `simEngagement`. The rate is the clock's own, read live; nothing here writes
  // it but `pick`, which is the same call the control's buttons make.
  scene.userData['simWarp'] = {
    /** Simulated seconds per real second, this instant. */
    get rate(): number { return gameClock().rate; },
    /** The level picked; the rate eases onto it. */
    get target(): number { return gameClock().warpTarget; },
    /** Index of the picked level in `levels`. */
    get pick(): number { return warp.pickIdx; },
    levels: WARP_LEVELS.map((l) => ({ rate: l.rate, label: l.label })),
    rampSeconds: WARP_RAMP_SECONDS,
    /** True while `?flightwarp=` or `setWarp()` holds the rate and nothing drops it. */
    get pinned(): boolean { return warp.pinned; },
    /** True on the `?warpstick=1` variant, where the drops are suppressed. */
    stuck: warpStick(),
    /** True while the clock is held at real time by a fight or by the heat
     *  ladder; the pick survives either and the ramp resumes once both let go. */
    get held(): boolean { return gameClock().held; },
    /** True on `?clockconst=1`, where a fight does not hold the rate at all. */
    clockConst: CLOCK_CONST,
    /** True on `?warpanyaxis=1`, where turning alone counts as thrust again. */
    anyAxis: warpAnyAxis(),
    /** True on `?warpstalerate=1`, where a frame's dt is derived from the rate
     *  before that same frame's drop rather than after. */
    staleRate: warpStaleRate(),
    /** True on `?warpthrustdrop=1`, where any thrust drops the clock to real time. */
    thrustDrop: warpThrustDrop(),
    /** True on `?viewwarpfrozen=1`, where the Solar System branch skips the policy. */
    viewFrozen: viewWarpFrozen(),
    /** True on `?bodiespinned=1`, where the registry is held at the boot epoch. */
    bodiesPinned: BODIES_PINNED,
    /** True on `?flightcarryoldparent=1`, where the frame's parent is picked
     *  after the carry rather than before it. */
    carryOldParent: FLIGHT_CARRY_OLD_PARENT,
    /** True on `?flightcentresheld=1`, where every pull centre is held still for
     *  the whole frame instead of moving across its spans. */
    centresHeld: FLIGHT_CENTRES_HELD,
    /** True on `?markerstale=1`, where the ship holds still against its parent
     *  while the Solar System view has the screen. */
    markerStale: MARKER_STALE,
    /** True on `?linestale=1`, where the predicted path stops being rebuilt
     *  while the Solar System view has the screen. */
    lineStale: LINE_STALE,
    /** True on `?lineanchored=1`, where the line is re-anchored only on a
     *  rebuild rather than carried on its parent every frame. */
    lineAnchored: LINE_ANCHORED,
    /** True on `?linelagframe=1`, where the line hangs on its parent's place a frame late. */
    lineLagFrame: LINE_LAG_FRAME,
    /** The highest level reachable this instant; the max of the thrust and
     *  approach rules' own drops holds the top one back. */
    get ceiling(): number { return warp.ceilingIdx(); },
    /** Envelope-widths out the approach rule engages, closing only. */
    approachFactor: WARP_APPROACH_FACTOR,
    /** True on `?warpapproachnoceiling=1`, where the approach rule is dropped. */
    approachNoCeiling: warpApproachNoCeiling(),
    /** True on `?warpdroppoint=1`, where the drop is a point test again. */
    dropPoint: warpDropPoint(),
    /** True on `?warpsweephelio=1`, where the drop sweeps heliocentric metres. */
    sweepHelio: warpSweepHelio(),
    /** How close the last drop test read the ship coming to the dominant body's
     *  centre, in the frame it swept. Null until the first test has run. */
    get sweepMinM(): number | null { return warp.sweepMinM; },
    /** What the level readout and the drop notice say right now. */
    get levelText(): string | null { return warp.readout.levelText; },
    get noticeText(): string | null { return warp.readout.noticeText; },
    /** The dominant body's descent envelope and where the ship is against it. */
    envelope(): WarpEnvelope | null {
      readFlightPulls();
      return warp.envelope();
    },
    /** The same call a button makes, refusals included. */
    setPick(idx: number): void { warp.setPick(idx); warp.paint(); },
    /** Whether a level (index into `levels`) could be picked right now. */
    reachable(idx: number): boolean { return warp.reachable(idx); },
    /** Why `reachable(idx)` is false right now, or null. */
    reason(idx: number): string | null { return warp.reason(idx); },
  };

  return { warp };
}
